import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import inventoryService, { Station } from '../../services/inventoryService';
import locationService, { Location } from '../../services/locationService';
import StationFormModal from './StationFormModal.tsx';
import BulkProductAssignment from './BulkProductAssignment.tsx';
import { wsService } from '../../services/websocket';
import { getStationStatusBadgeClass } from '../../utils/statusColors';
import { useLanguage } from '../../i18n/LanguageContext';

const StationManagement: React.FC = () => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [locationFilter, setLocationFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStation, setSelectedStation] = useState<Station | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBulkAssignmentOpen, setIsBulkAssignmentOpen] = useState(false);

  // Fetch stations
  const { data: stations, isLoading: stationsLoading } = useQuery(
    ['stations', locationFilter, statusFilter],
    () => inventoryService.getStations({
      location: locationFilter || undefined,
      status: statusFilter || undefined,
    })
  );

  // Fetch production locations from location service
  const { data: locationsResponse } = useQuery('locations-for-filter', () =>
    locationService.getLocations({ active: true, limit: 1000 })
  );

  const locations = locationsResponse?.data?.locations || [];

  // WebSocket for real-time updates
  useEffect(() => {
    wsService.connect();

    const handleStationUpdated = () => {
      queryClient.invalidateQueries(['stations']);
    };

    const handleSessionUpdated = () => {
      queryClient.invalidateQueries(['stations']);
    };

    wsService.on('station-updated', handleStationUpdated);
    wsService.on('production-session-updated', handleSessionUpdated);
    wsService.on('production-session-created', handleSessionUpdated);

    return () => {
      wsService.off('station-updated', handleStationUpdated);
      wsService.off('production-session-updated', handleSessionUpdated);
      wsService.off('production-session-created', handleSessionUpdated);
    };
  }, [queryClient]);

  // Delete station mutation
  const deleteMutation = useMutation(
    (id: number) => inventoryService.deleteStation(id),
    {
      onSuccess: () => {
        toast.success('Station deleted successfully');
        queryClient.invalidateQueries('stations');
        setIsDeleting(false);
      },
      onError: () => {
        toast.error('Failed to delete station');
        setIsDeleting(false);
      },
    }
  );

  // Update status mutation
  const updateStatusMutation = useMutation(
    ({ id, status }: { id: number; status: 'active' | 'inactive' | 'offline' }) =>
      inventoryService.updateStationStatus(id, status),
    {
      onSuccess: () => {
        toast.success('Station status updated');
        queryClient.invalidateQueries('stations');
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.error || error?.message || 'Failed to update station status';
        toast.error(errorMessage);
      },
    }
  );

  // Group stations by location
  const groupedStations = React.useMemo(() => {
    if (!stations) return {};
    return stations.reduce((acc: Record<string, Station[]>, station: Station) => {
      const key = `${station.location_code} - ${station.location_name}`;
      if (!acc[key]) acc[key] = [];
      acc[key].push(station);
      return acc;
    }, {});
  }, [stations]);

  const handleAddStation = () => {
    setSelectedStation(null);
    setIsModalOpen(true);
  };

  const handleEditStation = (station: Station) => {
    setSelectedStation(station);
    setIsModalOpen(true);
  };

  const handleDeleteStation = async (station: Station) => {
    setIsDeleting(true);
    deleteMutation.mutate(station.station_id);
  };

  const handleToggleStatus = (station: Station) => {
    const newStatus = station.status === 'active' ? 'inactive' : 'active';
    
    // If trying to deactivate, check for active sessions and warn user
    if (newStatus === 'inactive' && station.assigned_product_name) {
      toast.error(
        `Cannot deactivate station with active session. Product: ${station.assigned_product_name} (${station.assigned_product_sku}). Status: ${t(`status.${station.session_status}`) || station.session_status}. Please complete or cancel the session first.`,
        { duration: 5000 }
      );
      return;
    }
    
    updateStatusMutation.mutate({ id: station.station_id, status: newStatus });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Station Management</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage production stations across all locations
          </p>
        </div>
        <div className="flex gap-2 sm:gap-3">
          <button
            onClick={() => setIsBulkAssignmentOpen(true)}
            className="px-3 sm:px-4 py-2 text-sm sm:text-base bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
          >
            <span className="hidden sm:inline">Bulk Assign Products</span>
            <span className="sm:hidden">Bulk Assign</span>
          </button>
          <button
            onClick={handleAddStation}
            className="px-3 sm:px-4 py-2 text-sm sm:text-base bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
          >
            + Add Station
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="location" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Location
            </label>
            <select
              id="location"
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Locations</option>
              {locations?.map((loc: Location) => (
                <option key={loc.locationCode} value={loc.locationCode}>
                  {loc.description} ({loc.locationCode})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Status
            </label>
            <select
              id="status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="paused">Paused</option>
              <option value="idle">Idle</option>
            </select>
          </div>

          <div className="flex items-end">
            <div className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md p-3">
              <div className="text-xs text-gray-500 dark:text-gray-400">Total Stations</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{stations?.length || 0}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Stations by Location */}
      {stationsLoading ? (
        <div className="bg-white shadow rounded-lg p-8 text-center text-gray-500">
          Loading stations...
        </div>
      ) : Object.keys(groupedStations).length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center text-gray-500">
          No stations found. Click "Add Station" to create your first station.
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedStations).map(([locationName, locationStations]) => (
            <div key={locationName} className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
              {/* Location Header */}
              <div className="bg-indigo-50 dark:bg-indigo-900/30 px-6 py-4 border-b border-indigo-100 dark:border-indigo-700">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{locationName}</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                      {locationStations.length} {locationStations.length === 1 ? 'station' : 'stations'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Stations Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Station ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Station Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Warehouse
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Assigned Product
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {locationStations.map((station: Station) => (
                      <tr key={station.station_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          #{station.station_id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          {station.station_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {station.station_type || 'Production'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {station.warehouse_code}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                          {station.assigned_product_name ? (
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white">{station.assigned_product_name}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">{station.assigned_product_sku}</div>
                            </div>
                          ) : (
                            <span className="text-gray-400 italic">Not assigned</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStationStatusBadgeClass(station.status)}`}>
                            {station.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleEditStation(station)}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleToggleStatus(station)}
                              className={`${
                                station.status === 'active' ? 'text-yellow-600 hover:text-yellow-900' : 'text-green-600 hover:text-green-900'
                              }`}
                              disabled={updateStatusMutation.isLoading}
                            >
                              {station.status === 'active' ? 'Deactivate' : 'Activate'}
                            </button>
                            <button
                              onClick={() => handleDeleteStation(station)}
                              className="text-red-600 hover:text-red-900"
                              disabled={isDeleting}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Station Form Modal */}
      {isModalOpen && (
        <StationFormModal
          station={selectedStation}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedStation(null);
          }}
          onSuccess={() => {
            queryClient.invalidateQueries('stations');
            setIsModalOpen(false);
            setSelectedStation(null);
          }}
        />
      )}

      {/* Bulk Product Assignment Modal */}
      {isBulkAssignmentOpen && (
        <BulkProductAssignment
          onClose={() => setIsBulkAssignmentOpen(false)}
          onSuccess={() => {
            queryClient.invalidateQueries('stations');
            setIsBulkAssignmentOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default StationManagement;
